import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter,
  Button, TextInput, NumberInput, Select, Alert,
} from '@parsina/framework';
import { ExternalLink } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { autoDir, useTranslation } from '../../../i18n/I18nProvider.jsx';
import { clinicPanelUrl } from '../../../lib/runtimeEnv.js';
import {
  parseTariff, currencyName, currencyInfo, DEFAULT_CURRENCY,
} from '../../../lib/money.js';
import { APPLICABILITIES, CHART_EFFECTS } from '../Visit.shared.js';

// «خدمت جدید» — inventing a service from the chair (D3).
//
// ── What this is NOT ────────────────────────────────────────────────────────
// It is not the clinic app's service drawer. That one is 1,215 lines with a
// separate 1,068-line phone twin, and it shows margins, consumables and other
// people's commission — none of which an associate dentist should be reading
// mid-visit, and none of which they need in order to record a treatment.
//
// So: what a service IS, and what it costs. مواد مصرفی، پورسانت و بیمه stay in
// «کالا و خدمات», one link away, and the link is in this dialog so the trip is
// obvious rather than discovered.
//
// ── This saves to the CLINIC ────────────────────────────────────────────────
// Not to this visit and not to this dentist: the service joins «کالا و خدمات»
// and every colleague is offered it from then on, at the tariff typed here.
// That is the product owner's decision (D3, «everyone who can enter visit
// page»), and the dialog says so out loud rather than letting it be a surprise
// the clinic manager finds in a list next month.
//
// ── Money ───────────────────────────────────────────────────────────────────
// The field is in the CLINIC's currency, as a human quotes it: TOMAN for an
// Iranian clinic (IRR), dirhams with their two decimals for an AED one. The wire
// is whole MINOR UNITS of that currency — whole rials for IRR — like every
// Parsina service. The kit owns that conversion (lib/money.js → `parseAmount`)
// and this file does not re-derive it — a rial figure under a toman label reads
// as ten times the money, and a toman guess on a dirham clinic as a tenth.
//
// The currency is not an argument to the mutation: the BFF stores the tariff in
// the clinic's own. So a field whose currency this screen cannot tell (`null`)
// takes NO tariff rather than guessing one — the service is saved «بدون تعرفه»
// and priced in the clinic panel.

export default function NewServiceDialog({
  open, onOpenChange, onCreate, saving, t, clinicId,
  // Prefilled from what the dentist was already doing: they pressed «خدمت
  // جدید» because the list did not have what they were looking for.
  initialName = '',
  // The clinic's currency (an ISO code), or null when it is not known. Omitted
  // is IRR — what this dialog was before currencies, byte for byte.
  currency = DEFAULT_CURRENCY,
}) {
  const { locale } = useTranslation();
  const known = currency != null;
  const isIrr = known && currencyInfo(currency).code === DEFAULT_CURRENCY;
  const unit = known ? currencyName(currency, { t, locale }) : null;
  const [form, setForm] = React.useState({
    name: '', category: '', applicability: '', chartEffect: '',
    priceToman: '', durationMinutes: 30,
  });
  const [failure, setFailure] = React.useState(null);
  // Only after the dentist has left the field: an error on a form they have not
  // filled in yet is a telling-off, not help.
  const [touched, setTouched] = React.useState(false);

  // Reset on every open, not on mount: the dialog is kept mounted by its
  // parent, so a stale draft would otherwise greet the next service.
  React.useEffect(() => {
    if (!open) return;
    setForm({
      name: initialName, category: '', applicability: '', chartEffect: '',
      priceToman: '', durationMinutes: 30,
    });
    setFailure(null);
    setTouched(false);
  }, [open, initialName]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setFailure(null);
    const name = form.name.trim();
    if (!name) {
      setTouched(true);
      return;
    }

    // An empty field is «بدون تعرفه», not zero-because-unparseable: the dentist
    // saves the service now and the clinic prices it later. Anything typed that
    // is NOT a number is a mistake worth stopping on, because the alternative
    // is silently saving a service as free.
    // `parseAmount`, not a bare Number(): the app renders Persian numerals
    // everywhere and a dentist types «۵۰۰٬۰۰۰». For IRR it is exactly
    // `parseTomanToRial` (toman in, whole rials out); for any other currency it
    // is strict — at most the currency's own decimals, grouping in threes — so
    // «12.505» dirhams is refused rather than rounded into a price nobody typed.
    // The panel language says which mark is the decimal: a French or German
    // dentist types «12,50», which without it was refused for a dirham clinic.
    // For toman, which has no fraction a dentist types, `parseTariff` reads a
    // French «500,000» or German «500.000» as the half million typed and
    // refuses the ambiguous «12,5» rather than saving a price ten or a thousand
    // times off. fa and en parse exactly as before.
    const typed = known ? String(form.priceToman).trim() : '';
    const tariffRial = typed ? parseTariff(typed, currency, locale) : 0;
    if (!Number.isFinite(tariffRial) || tariffRial < 0) {
      setFailure({ message: t('visit.plan.newService.badTariff', { unit }) });
      return;
    }

    const err = await onCreate({
      name,
      category: form.category.trim() || null,
      applicability: form.applicability || null,
      chartEffect: form.chartEffect || null,
      // Whole minor units (whole rials for IRR). A half-toman entry rounds here
      // rather than being refused by the BFF with a message naming a unit
      // («ریال») the dentist never saw; any other currency is already whole.
      tariffRial: Math.round(tariffRial),
      durationMinutes: Math.max(1, Number(form.durationMinutes) || 30),
    });
    if (err) {
      setFailure(err);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-service-dialog">
        <DialogHeader>
          <DialogTitle>{t('visit.plan.newService.title')}</DialogTitle>
          {/* Said before the form, not after it: that this is saved to the
              clinic changes whether somebody wants to fill it in. */}
          <DialogDescription>{t('visit.plan.newService.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {failure && (
              <Alert variant="error">
                {failure.message || t('visit.plan.newService.failed')}
              </Alert>
            )}

            <TextInput
              label={t('visit.plan.newService.name')}
              aria-label={t('visit.plan.newService.name')}
              required
              dir={autoDir(form.name)}
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              // Said at the field rather than only by a disabled button: a save
              // that does nothing and explains nothing is the worst of the three
              // possible answers.
              error={touched && !form.name.trim() ? t('visit.plan.newService.nameRequired') : ''}
              onBlur={() => setTouched(true)}
            />

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10,
            }}>
              <TextInput
                label={`${t('visit.plan.newService.category')} ${t('common.form.optional')}`}
                aria-label={t('visit.plan.newService.category')}
                dir={autoDir(form.category)}
                value={form.category}
                onChange={(e) => set({ category: e.target.value })}
              />

              {/* The one field that exists only because this is a dental app:
                  it decides whether the plan form asks for a tooth. */}
              <Select
                label={`${t('visit.plan.newService.applicability')} ${t('common.form.optional')}`}
                aria-label={t('visit.plan.newService.applicability')}
                placeholder={null}
                value={form.applicability}
                onChange={(e) => set({ applicability: e.target.value })}
                options={APPLICABILITIES(t)}
              />

              {/* And the second dental-only field: what the service DOES to
                  that tooth. Without it a clinic-invented عصب‌کشی would be the
                  one service that never proposes a chart change — the exact
                  hole the curated catalogue just filled. */}
              <Select
                label={`${t('visit.plan.newService.chartEffect')} ${t('common.form.optional')}`}
                aria-label={t('visit.plan.newService.chartEffect')}
                placeholder={null}
                value={form.chartEffect}
                onChange={(e) => set({ chartEffect: e.target.value })}
                options={CHART_EFFECTS(t)}
              />
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10,
            }}>
              {/* In the clinic's currency, named in the label: «(تومان)» for
                  IRR exactly as before, «(درهم امارات)» for an AED clinic —
                  whose figure has decimals, so the keypad offers the mark. */}
              {known && (
                <TextInput
                  label={`${t('visit.plan.newService.tariff', { unit })} ${t('common.form.optional')}`}
                  aria-label={t('visit.plan.newService.tariff', { unit })}
                  dir="ltr"
                  inputMode={isIrr ? 'numeric' : 'decimal'}
                  value={form.priceToman}
                  placeholder={t('visit.plan.noTariff')}
                  onChange={(e) => set({ priceToman: e.target.value })}
                  data-testid="new-service-tariff"
                  data-currency={currency}
                />
              )}
              <NumberInput
                label={t('visit.plan.newService.duration')}
                aria-label={t('visit.plan.newService.duration')}
                min={1}
                value={form.durationMinutes}
                onChange={(e) => set({ durationMinutes: e.target.value })}
              />
            </div>

            <span style={{ fontSize: 11.5, color: C.textMuted, lineHeight: 1.8 }}>
              {known
                ? t('visit.plan.newService.tariffHint')
                : t('visit.plan.newService.currencyUnknown')}
            </span>

            {/* What this form deliberately does not do, and where it is done. */}
            <div style={{
              background: CT.surface2, borderRadius: 10, padding: '10px 12px',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>
                {t('visit.plan.newService.clinicOnly.title')}
              </span>
              <span style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.8 }}>
                {t('visit.plan.newService.clinicOnly.body')}
              </span>
              <a
                href={clinicPanelUrl(clinicId)}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 44,
                  fontSize: 12.5, fontWeight: 700, color: CT.accentInk,
                }}
              >
                {t('visit.plan.noServices.action')}
                <ExternalLink size={13} aria-hidden />
              </a>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            className="min-h-11"
            onClick={submit}
            disabled={saving || !form.name.trim()}
            data-testid="new-service-save"
          >
            {saving ? t('common.actions.saving') : t('visit.plan.newService.save')}
          </Button>
          <Button
            className="min-h-11"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {t('common.actions.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
